<template>
    <div class="activitiy-body">
        <page-loader
            class="mb-2 mt-2"
            :loading="loading"
        />
        <template v-if="Object.keys(activityLogProcessed).length > 0">
            <b-row
                v-for="(activities, date) in activityLogProcessed"
                :key="'activity'+date"
                class="mt-2"
                align-v="center"
            >
                <b-col class="activity-summary">
                    <p class="align-middle" v-for="activity in activities">
                        <span v-html="activity.message"/> <span class="text-muted">{{ activity.createdAtMonthDay }}</span>
                    </p>
                </b-col>
            </b-row>
        </template>
        <b-row
            v-else-if="!loading"
            class="mb-2 mt-2"
            align-v="center"
        >
            <div class="d-flex justify-content-center align-items-center">
                No activity recorded for this order.
            </div>
        </b-row>
    </div>
</template>

<script>
import './ActivityLogDetails.scss';
import dayjs from 'dayjs';
import get from 'lodash/get';
import {mapState} from 'pinia';
import {useAuthStore} from '@store';
import {AppResourceSearch, ApiFilter} from '@sellers-shield-home-protection/ss-core-js';
import HomeSaleLegalProtectionAppResource
    from "@sellers-shield-home-protection/ss-core-js/src/AppResources/HomeSaleLegalProtectionAppResource";
import {AGENT} from "@sellers-shield-home-protection/ss-core-js/src/AppResources/LuAccountTypeAppResource";
import InvitationAppResource from "@sellers-shield-home-protection/ss-core-js/src/AppResources/InvitationAppResource";
import OrderPropertyAppResource from "@sellers-shield-home-protection/ss-core-js/src/AppResources/OrderPropertyAppResource";
import EscrowOfficerAppResource from "@sellers-shield-home-protection/ss-core-js/src/AppResources/EscrowOfficerAppResource";
import SellerAppResource from "@sellers-shield-home-protection/ss-core-js/src/AppResources/SellerAppResource";
import {setupEngine} from "@helpers/api-helper";

export default {
    name: 'ActivityLogDetails',
    props: {
        orderAppResource: {
            type: Object,
        },
    },
    data: () => {
        return {
            loading: false,
            activityLogRecords: [],
            orderInviteMetaMap: {
                property: {
                    streetAddress: 'meta.property.address',
                    city: 'meta.property.city',
                    zip: 'meta.property.zip',
                    stateId: 'meta.property.state',
                },
                homeSaleLegalProtection: {
                    legalProtectionAmount: 'meta.hslp.legalProtectionAmount',
                    yearsLegalProtection: 'meta.hslp.yearsLegalProtection',
                    tier: 'meta.hslp.tier',
                    upsellPrice: 'meta.hslp.price',
                    upsellPaymentTermId: 'meta.hslp.paymentTermId',
                    paymentFromId: 'meta.hslp.paymentFromId',
                },
                escrowOfficer: {
                    fullName: 'meta.payAtCloseInputs.escrowOfficerName',
                    email: 'meta.payAtCloseInputs.escrowOfficerEmail',
                },
                primarySeller: {
                    email: 'meta.primarySeller.email',
                    firstName: 'meta.primarySeller.firstName',
                    lastName: 'meta.primarySeller.lastName',
                    phone: 'meta.primarySeller.phone',
                },
                secondarySeller: {
                    email: 'meta.secondarySeller.email',
                    firstName: 'meta.secondarySeller.firstName',
                    lastName: 'meta.secondarySeller.lastName',
                    phone: 'meta.secondarySeller.phone',
                },
            },
        };
    },
    computed: {
        ...mapState(useAuthStore, {
            me: 'me',
        }),

        subjectResourcesFlatTreeData() {
            let subject = [];
            if (this.orderAppResource?.uuid) {
                subject = [
                    ...subject,
                    {path: '', type: 'order', primaryKey: 'uuid', primaryValue: this.orderAppResource?.uuid},
                    ...(this.orderAppResource?.relationObjectFlatTreeData ? this.orderAppResource.relationObjectFlatTreeData(['user', 'form'], true) : [])
                ];
            }
            return subject.filter(obj => !!obj.primaryKey && !!obj.primaryValue && obj.type.indexOf('lu-') !== 0 && obj.type != 'WildAppResource');
        },

        causerResourcesFlatTreeData() {
            let ordRes = this.orderAppResource,
                ordRels = this.orderAppResourceRelations,
                causerMe = ordRes?.uuid && [ordRes?.agentUuid, ordRes?.brokerUuid, ordRels?.primarySeller?.userUuid, ordRels?.secondarySeller?.userUuid].includes(this.me.uuid),
                causers = causerMe ? [] : [{path: 'me', type: 'user', primaryKey: 'uuid', primaryValue: this.me.uuid}];

            if (ordRes?.uuid) {
                causers = [
                    ...causers,
                    ...[
                        {
                            path: 'agent', type: 'user', primaryKey: 'uuid', primaryValue: ordRes?.agentUuid,
                            fullName: ordRes?.agent?.firstName || ordRes?.agent?.lastName ? (`${ordRes?.agent?.firstName || ''} ${ordRes?.agent?.lastName || ''}`).trim() : 'Agent'
                        },
                        {
                            path: 'broker', type: 'broker', primaryKey: 'uuid', primaryValue: ordRes?.brokerUuid,
                            fullName: `${ordRes?.broker?.companyName}` || 'Broker'
                        },
                        !ordRels?.primarySeller?.userUuid ? null : {
                            path: 'seller',
                            type: 'user',
                            primaryKey: 'uuid',
                            primaryValue: ordRels.primarySeller.userUuid,
                            fullName: this.getSellerName('primarySeller', 'Primary seller'),
                        },
                        !ordRels?.secondarySeller?.userUuid ? null : {
                            path: 'seller',
                            type: 'user',
                            primaryKey: 'uuid',
                            primaryValue: ordRels.secondarySeller.userUuid,
                            fullName: this.getSellerName('secondarySeller', 'Secondary seller'),
                        },
                    ]
                ];
            }
            return causers.filter(obj => obj && !!obj.primaryKey && !!obj.primaryValue);
        },

        activityLogProcessed() {
            let returnData = {}, exists = {}, orderCreatedInfo;

            (this.activityLogRecords || [])
                .filter(d => !!d.createdAt)
                .map(d => this.processActivityLogRow(d))
                .forEach(info => {
                    if (Boolean(info)) {
                        if (info.message.indexOf('created ') > -1 && (info.message.indexOf(' order') > -1 || info.message.indexOf(' disclosure') > -1)) {
                            orderCreatedInfo = info;
                        } else if (!exists[info.datetime + info.message]) {
                            !!returnData[info.date] || (returnData[info.date] = []);
                            returnData[info.date].unshift(info);
                            exists[info.datetime + info.message] = true;
                        }
                    }
                });

            if (!!orderCreatedInfo) {
                !!returnData[orderCreatedInfo.date] || (returnData[orderCreatedInfo.date] = []);
                returnData[orderCreatedInfo.date].push(orderCreatedInfo);
            }

            return Object
                .keys(returnData)
                .reverse()
                .reduce((r, k) => (r[k] = returnData[k], r), {})
                ;
        },
        orderAppResourceRelations() {
            return {
                invitation: this.getInvitation(this.orderAppResource || {}),
                property: this.getProperty(this.orderAppResource || {}),
                homeSaleLegalProtection: this.getHSLP(this.orderAppResource || {}),
                escrowOfficer: this.getEscrowOfficer(this.orderAppResource || {}),
                primarySeller: this.getSeller(this.orderAppResource || {}, 'primary'),
                secondarySeller: this.getSeller(this.orderAppResource || {}, 'secondary'),
            }
        }
    },

    methods: {
        getSellerName(type, defText = 'Seller') {
            let ordRels = this.orderAppResourceRelations;
            return (
                `${ordRels[type]?.user?.firstName || ordRels[type]?.firstName || ''} ${ordRels[type]?.user?.lastName || ordRels[type]?.lastName || ''}`
            ).trim() || defText;
        },

        getModelDataFromInvite(appResource, key) {
            let map = this.orderInviteMetaMap[key] || {};
            if (typeof map === 'string' || map instanceof String) {
                return get(appResource, `invitation.${map}`, {});
            }

            let mapped = {...map};
            Object.keys(mapped).forEach(key => {
                mapped[key] = get(appResource, `invitation.${mapped[key]}`, '');
            });
            return mapped;
        },

        getHSLP(appResource) {
            let homeSaleLegalProtection = appResource.homeSaleLegalProtection;
            if (!homeSaleLegalProtection) {
                homeSaleLegalProtection = new HomeSaleLegalProtectionAppResource({
                    attributes: {
                        upsellPurchasedById: appResource.orderedById || AGENT,
                        ...this.getModelDataFromInvite(appResource, 'homeSaleLegalProtection'),
                    }
                });
            }
            return homeSaleLegalProtection;
        },

        getInvitation(appResource) {
            let invitation = appResource.invitation;
            if (!invitation) {
                invitation = new InvitationAppResource({});
            }
            return invitation;
        },

        getProperty(appResource) {
            let property = appResource.property;
            if (!property) {
                property = new OrderPropertyAppResource({
                    attributes: this.getModelDataFromInvite(appResource, 'property'),
                });
            }
            return property;
        },

        getEscrowOfficer(appResource) {
            let escrowOfficer = appResource.escrowOfficer;
            if (!escrowOfficer) {
                escrowOfficer = new EscrowOfficerAppResource({
                    attributes: this.getModelDataFromInvite(appResource, 'escrowOfficer'),
                });
            }
            return escrowOfficer;
        },

        getSeller(appResource, type) {
            let seller = (appResource.sellers ? appResource.sellers.toArray() : []).filter(seller => seller.type === type).pop();

            if (!seller) {
                seller = new SellerAppResource({
                    attributes: {
                        type: type,
                        ...this.getModelDataFromInvite(appResource, `${type.toLowerCase()}Seller`),
                    }
                });
            }

            return seller;
        },

        ucwords(phrase) {
            return phrase
                .toLowerCase()
                .replace(/\b[a-z]/g, function (l) {
                    return l.toUpperCase();
                });
        },
        whoDidIt(identifier, type, subjectId = null, subjectType = null) {
            if (type === 'user' && identifier === this.me?.uuid) {
                return 'You';
            }

            let foundCauser, def;

            if (!identifier && subjectId) {
                switch (subjectType) {
                    case 'seller':
                        let ordRels = this.orderAppResourceRelations;
                        def = [ordRels?.primarySeller?.uuid, ordRels?.primarySeller?._uuid].includes(subjectId)
                            ? 'primarySeller'
                            : [ordRels?.secondarySeller?.uuid, ordRels?.secondarySeller?._uuid].includes(subjectId)
                                ? 'secondarySeller'
                                : 'Seller';

                        if (def !== 'Seller') {
                            foundCauser = {
                                fullname: ordRels[def]?.userUuid !== this.me?.uuid
                                    ? 'You'
                                    : this.getSellerName(def, def = def.replace('S', ' s'))
                            };
                        }
                        break;
                    case 'jot-form-disclosure-session':
                        let sessions = this.orderAppResource?.disclosure?.jotFormDisclosureSessions;
                        identifier = ((sessions ? sessions.toArray() : [] || []).find(
                            jfd => jfd.uuid === subjectId
                        ) || {}).userUuid;

                        type = 'user';
                        def = identifier !== this.me?.uuid ? 'Seller' : 'You';
                        break;
                }
            }

            if (!foundCauser?.fullName) {
                def = def || this.me?.uuid === this.orderAppResource?.agentUuid ? 'You' : 'Agent';
                foundCauser = !identifier
                    ? {}
                    : (this.causerResourcesFlatTreeData || []).find(
                    row => [row.type, row.path].includes(type) && row.primaryValue === identifier
                ) || {};
            }

            return foundCauser?.fullName || def;
        },
        processActivityLogRow(row) {
            if (!(row.subjectType && row.createdAt && (row.causerId && row.causerType || row.customMessage))) {
                return null;
            }
            let returnMessage,
                rowCreatedAtMonthDay = dayjs(row.createdAt).format('MMMM DD [`]YY'),
                rowCreatedAt = dayjs(row.createdAt).format('MMMM DD YYYY [at] h:mma'),
                subjectType = this.ucwords(row.subjectType.split('\\').reverse()[0].replace(/[-_]+/g, ' ')),
                causerUser = this.ucwords(this.whoDidIt(row.causerId, row.causerType, row.subjectId, row.subjectType))
            ;
            try {
                if (row.customMessage) {
                    //custom messages generated based on certain conditions or trigger events
                    returnMessage = `${row.customMessage}`;
                    let toReplace = returnMessage.match(/\{([a-z_\.]+)\}/ig) || [];
                    toReplace.forEach(pathKey => {
                        let value, path = pathKey.replace(/[\{\}]+/g, '');

                        if (path === 'form') {
                            let sessions = this.orderAppResource?.disclosure?.jotFormDisclosureSessions,
                                form = ((sessions ? sessions.toArray() : [] || []).find(
                                    jfd => jfd.uuid === (row.subjectType === 'jot-form-disclosure-session' ? row.subjectId : row?.attributes?.jot_form_disclosure_uuid)
                                ) || {}).form || {};
                            value = form?.officialName || form?.name || 'form';
                        } else {
                            value = ['causerUser', 'user', 'userAgent', 'seller'].includes(path)
                                ? causerUser
                                : get(this.orderAppResourceRelations, path, null);

                            if (!value) {
                                value = get(this.orderAppResource, path, path.split('.')[0]);
                            }
                        }
                        returnMessage = returnMessage.replace(
                            pathKey,
                            value?.constructor?.name == 'Moment'
                                ? '<b>' + value.format('MM/DD/YYYY') + '</b>'
                                : `<b>${value}</b>`
                        );
                    });
                    returnMessage = returnMessage.trim();
                } else {
                    let testNonValid = />(?:(?:uu|zoho_|order_uu)?id|(?:upsell_purchased|created|updated)_(?:at|by_id|term_id))</g,
                        attrs = get(row, 'attributes.properties.attributes', {}),
                        hasData = Object.keys(
                            Object.entries(attrs).reduce((a, [k, v]) => (v && !`>${k}<`.match(testNonValid) ? (a[k] = v, a) : a), {})
                        ),

                        old = get(row, 'attributes.properties.old', {}),
                        hadData = Object.keys(
                            Object.entries(old).reduce((a, [k, v]) => (v && !`>${k}<`.match(testNonValid) ? (a[k] = v, a) : a), {})
                        );

                    //standard simple & composed messages
                    switch (row.description) {
                        case 'created':
                            if (['order', 'home-sale-legal-protection'].includes(row.subjectType)) {
                                if (hasData.filter(a => a !== 'meta').length == 0) break;
                            }
                            if (Boolean(row.causerId) && hasData.length > 0) {
                                returnMessage = row.subjectType === 'order'
                                    ? `${causerUser} created order.`
                                    : `${causerUser} added ${subjectType} details to the order.`;
                            }
                            break;
                        case 'updated':
                            let updated = hasData.filter(a => a !== 'meta').join(', ')
                                .replace(/_uuid|_id/ig, '')
                                .replace(/_/ig, ' ')
                                .trim();

                            if (Boolean(row.causerId) && hadData.length === 0) {
                                returnMessage = `${causerUser} added ${subjectType} details to the order.`;
                            } else if (updated || !Boolean(row.causerId) && hadData.filter(a => a !== 'meta').length > 0) {
                                if (updated && !['order', 'seller', 'order-seller', 'order-property', 'escrow-officer'].includes(row.subjectType)) {
                                    updated = null;
                                }
                                returnMessage = updated
                                    ? `${causerUser} changed ${subjectType} details: <b>${updated}</b>.`
                                    : `${causerUser} updated ${subjectType} details.`;
                            }
                            break;
                        case 'deleted':
                            returnMessage = causerUser + (
                                row.subjectType === 'order' ? ' cancelled order.' : ` removed ${subjectType} record from order.`
                            );
                            break;
                    }
                }
            } catch (e) {
                console.error(e);
            }

            return !!returnMessage ? {
                id: row.id,
                createdAtMonthDay: rowCreatedAtMonthDay,
                datetime: rowCreatedAt,
                date: rowCreatedAt.split(' at ')[0],
                time: rowCreatedAt.split(' at ')[1],
                message: returnMessage.replace(new RegExp(`${this.me?.firstName || 'skip'}[<\/b> ]+${this.me?.lastName || 'skip'}`, 'gi'), 'You')
            } : null;
        },

        getTypeFilters(type = 'causer') {
            return this[`${type}ResourcesFlatTreeData`].map(
                record => ({
                    [`${type}_type`]: record.type,
                    [`${type}_id`]: record.primaryValue
                })
            );
        },

        getSubjectFilter() {
            return new ApiFilter({
                andOr: 'or',
                filters: this.getTypeFilters('subject')
                    .map(record => new ApiFilter({
                        params: record
                    }))
            });
        },

        getCauserFilter() {
            return new ApiFilter({
                andOr: 'or',
                filters: [
                    new ApiFilter({
                        params: {
                            'causer_type': {operator: 'isNull', value: 'isNull'},
                            'causer_id': {operator: 'isNull', value: 'isNull'},
                        }
                    }),
                    ...this.getTypeFilters('causer')
                        .map(record => new ApiFilter({
                            params: record
                        }))
                ]
            });
        },

        async getActivityLogs() {
            this.loading = true;

            try {
                // TODO: setup elasticsearch, add scout to model and change here api path to `e-search` :(
                const appResourceSearch = new AppResourceSearch(
                    'activity-log', {apiPath: 'search'}
                );

                await appResourceSearch.setSearchData({
                    pagination: 'disabled',
                    filter: new ApiFilter({
                        andOr: 'or',
                        filters: [
                            new ApiFilter({
                                params: {
                                    'subject_type': 'order',
                                    'subject_id': this.orderAppResource.uuid
                                }
                            }),
                            new ApiFilter({
                                andOr: 'and',
                                filters: [
                                    this.getSubjectFilter(),
                                    this.getCauserFilter(),
                                ]
                            }),
                        ],
                    }),
                    orderBy: {
                        createdAt: 'asc'
                    }
                }, false);

                setupEngine();
                await appResourceSearch.search();

                this.activityLogRecords = appResourceSearch.appResourceCollection.toArray() || [];
            } catch (e) {
                this.activityLogRecords = [];
                console.error(e);
            }

            this.$nextTick(() => this.loading = false);
        }
    },
    async created() {
        !this.orderAppResource?.uuid || await this.getActivityLogs();
    }
}
</script>
